/**
 * components/admin/DeliveryPlatformTab.jsx · v2.7.57+E34 · Section 2
 * ═══════════════════════════════════════════════════════════════════════
 * Admin console surface for the DELIVERY PLATFORM. In Section 2 this
 * ships with the 5-variable pricing config card only — Section 3 will
 * add the dispatch board (delivery queue + driver-assignment dropdown +
 * Document Center) inside this same tab.
 *
 * RBAC (mirrors backend):
 *   - Read:  any staff read-tier admin can VIEW the pricing config
 *   - Write: only supervisor / operating_partner / developer / admin
 *            can change pricing (backend enforces via _require_admin_write)
 */
import { useCallback, useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import adminApi from "@/lib/adminApi";
import { toast } from "sonner";
import DeliveryDispatchBoard from "@/components/admin/DeliveryDispatchBoard";
import DeliveryPayoutLedger from "@/components/admin/DeliveryPayoutLedger";
import CourierApiIntegrationCard from "@/components/admin/CourierApiIntegrationCard";
import {
  Truck, DollarSign, Save, RefreshCw, Calculator, ShieldAlert, Landmark, Check,
} from "lucide-react";

const FIELDS = [
  { key: "base_delivery_fee",  label: "Base Delivery Fee",   suffix: "flat / order",    testid: "cfg-base"        },
  { key: "per_stop_fee",       label: "Per Stop Fee",        suffix: "× stops",         testid: "cfg-per-stop"    },
  { key: "per_mile_fee",       label: "Per Mile Fee",        suffix: "× miles",         testid: "cfg-per-mile"    },
  { key: "per_minute_fee",     label: "Per Minute Fee",      suffix: "× minutes",       testid: "cfg-per-minute"  },
  { key: "stop_wait_time_fee", label: "Stop Wait Time Fee",  suffix: "× stops",         testid: "cfg-wait"        },
  { key: "min_total",          label: "Minimum Total Floor", suffix: "hard floor",      testid: "cfg-min-total"   },
  // v2.7.63+E46 · Pricing Transparency
  { key: "tax_rate",           label: "Estimated Tax Rate",  suffix: "% of fee subtotal", testid: "cfg-tax-rate",  isPercent: true },
  { key: "driver_payout_ratio", label: "Driver Payout Ratio", suffix: "% of net earnings kept by driver", testid: "cfg-driver-ratio", isRatio: true },
];

// v2.7.63+E46.2 · Tax Zone Presets — one-click apply for common jurisdictions.
// Sourced from state DoR + municipal add-ons as of 2026-01. Operator can
// still hand-tune the % after selecting a preset for edge cases.
const TAX_PRESETS = [
  { id: "none",   label: "No Tax",           rate: 0.000,  hint: "Passthrough / merchant-of-record handles tax downstream" },
  { id: "ok_st", label: "Oklahoma State",   rate: 4.500,  hint: "Base state rate only — no municipal add-on" },
  { id: "okc",    label: "OKC Metro",        rate: 8.625,  hint: "State 4.5% + OK County 0.35% + OKC 4.125% (approx)" },
  { id: "tulsa",  label: "Tulsa Metro",      rate: 8.517,  hint: "State 4.5% + Tulsa County 0.367% + Tulsa 3.65% (approx)" },
  { id: "tx",     label: "Texas",            rate: 6.250,  hint: "State rate only — cities may add up to 2% (hand-edit if needed)" },
];

export default function DeliveryPlatformTab() {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  // v2.7.63+E46.6 · Auto-scroll to Dispatch Board when navigation
  // provides `#sub=queue`. v2.7.65+E51 · generalized: `#sub=payouts`
  // deep-links to the Delivery Payout Ledger (new "Delivery" nav group).
  const [scrollTarget, setScrollTarget] = useState(null);

  useEffect(() => {
    const parseHash = () => {
      const h = (window.location.hash || "").replace(/^#/, "");
      const sub = new URLSearchParams(h).get("sub");
      return sub === "queue" || sub === "payouts" ? sub : null;
    };
    setScrollTarget(parseHash());
    const onHash = () => setScrollTarget(parseHash());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  useEffect(() => {
    if (!scrollTarget || loading) return;
    // Give the target card a tick to mount, then scroll.
    const selector = scrollTarget === "payouts"
      ? '[data-testid="delivery-payout-ledger"]'
      : '[data-testid="delivery-dispatch-board"]';
    const t = setTimeout(() => {
      const el = document.querySelector(selector);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 250);
    return () => clearTimeout(t);
  }, [scrollTarget, loading]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminApi.get("/deliveries/pricing/config");
      setCfg(r.data);
      setForm(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Failed to load pricing");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const changed = cfg && FIELDS.some((f) => Number(form[f.key]) !== Number(cfg[f.key]));

  const update = (key) => (e) => {
    const v = e.target.value;
    setForm((s) => ({ ...s, [key]: v === "" ? "" : parseFloat(v) }));
  };

  const save = async () => {
    setBusy(true);
    try {
      const body = {};
      for (const f of FIELDS) {
        const v = parseFloat(form[f.key]);
        if (Number.isFinite(v) && v >= 0) body[f.key] = v;
      }
      const r = await adminApi.put("/deliveries/pricing/config", body);
      setCfg(r.data);
      setForm(r.data);
      toast.success("Delivery Platform pricing updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="delivery-platform-tab">
      {/* Header */}
      <Card className="bg-zinc-900 border-zinc-800 p-4">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-amber-400/15 border border-amber-400/40 flex items-center justify-center flex-shrink-0">
            <Truck className="h-6 w-6 text-amber-300"/>
          </div>
          <div className="min-w-0">
            <div className="text-lg font-extrabold text-zinc-100">Delivery Platform</div>
            <div className="text-xs text-zinc-400">
              Multi-stop courier logistics · Section 2 · Pricing engine + payment gateway
            </div>
          </div>
        </div>
      </Card>

      {/* Pricing config card */}
      <Card className="bg-zinc-900 border-zinc-800 p-4 sm:p-5" data-testid="delivery-pricing-card">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-sm font-bold text-zinc-100 flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-emerald-300"/>
              Pricing Configuration
            </div>
            <div className="text-[11px] text-zinc-500 mt-0.5">
              These 6 variables drive every quote. Last updated:{" "}
              <span className="text-zinc-300 font-mono">{cfg?.updated_at?.slice(0, 19).replace("T", " ") || "—"}</span>
              {cfg?.updated_by && (
                <> by <span className="text-zinc-300">{cfg.updated_by}</span></>
              )}
            </div>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={load}
            disabled={loading || busy}
            className="h-8 text-xs"
            data-testid="cfg-refresh"
          >
            <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? "animate-spin" : ""}`}/>
            Refresh
          </Button>
        </div>

        {loading ? (
          <div className="p-8 text-center text-sm text-zinc-500">Loading…</div>
        ) : (
          <>
            {/* v2.7.63+E46.2 · Tax Zone Presets — one-click jurisdictional
                tax_rate application. Highlights the currently-matched
                preset (rounded to 3 decimals) with a check icon. */}
            <div className="mb-4 p-3 rounded-lg border border-sky-400/30 bg-sky-500/5" data-testid="tax-presets-card">
              <div className="flex items-center gap-1.5 mb-2">
                <Landmark className="h-3.5 w-3.5 text-sky-300"/>
                <span className="text-[10px] uppercase tracking-widest text-sky-200 font-bold">
                  Tax Zone Presets · One-Click
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                {TAX_PRESETS.map((p) => {
                  const isActive = Math.round((Number(form.tax_rate) || 0) * 1000) === Math.round(p.rate * 1000);
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => {
                        setForm((s) => ({ ...s, tax_rate: p.rate }));
                        toast.success(`Tax Zone → ${p.label} (${p.rate.toFixed(3)}%)`);
                      }}
                      title={p.hint}
                      className={`px-3 py-1.5 rounded-full border text-[11px] font-bold transition ${
                        isActive
                          ? "bg-sky-500/25 border-sky-400 text-sky-100 shadow-inner"
                          : "bg-zinc-950 border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:border-sky-400/50"
                      }`}
                      data-testid={`tax-preset-${p.id}`}
                    >
                      {isActive && <Check className="h-3 w-3 mr-1 inline text-sky-300"/>}
                      {p.label} · <span className="meter-display font-mono">{p.rate.toFixed(3)}%</span>
                    </button>
                  );
                })}
              </div>
              <div className="text-[10px] text-zinc-500 mt-2">
                Applies to the Estimated Tax Rate field below. Save to persist. Rates are approximate —
                hand-tune for exact municipal add-ons.
              </div>
            </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {FIELDS.map((f) => {
              const isPct = f.isPercent || f.isRatio;
              return (
                <div key={f.key}>
                  <Label className="text-[10px] uppercase tracking-widest text-zinc-400">{f.label}</Label>
                  <div className="relative mt-1">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-amber-400 text-xs font-bold pointer-events-none">
                      {isPct ? "%" : "$"}
                    </span>
                    <Input
                      type="number"
                      step={f.isRatio ? "0.01" : "0.01"}
                      min="0"
                      max={f.isRatio ? "1" : undefined}
                      value={form[f.key] ?? ""}
                      onChange={update(f.key)}
                      data-testid={f.testid}
                      className="bg-zinc-950 border-zinc-700 h-10 pl-5 meter-display"
                    />
                  </div>
                  <div className="text-[10px] text-zinc-500 mt-1">{f.suffix}</div>
                </div>
              );
            })}
          </div>
          </>
        )}

        <div className="mt-4 flex justify-end">
          <Button
            onClick={save}
            disabled={!changed || busy || loading}
            className="bg-amber-400 hover:bg-amber-300 text-zinc-950 font-extrabold h-10"
            data-testid="cfg-save"
          >
            <Save className="h-4 w-4 mr-1"/>
            {busy ? "Saving…" : changed ? "Save Pricing" : "No Changes"}
          </Button>
        </div>
      </Card>

      {/* Formula reference · read-only for staff */}
      <Card className="bg-zinc-950 border-zinc-800 p-4" data-testid="delivery-formula-card">
        <div className="flex items-center gap-2 mb-2">
          <Calculator className="h-4 w-4 text-sky-300"/>
          <div className="text-sm font-bold text-zinc-100">Quote Formula</div>
        </div>
        <pre className="text-[11px] text-zinc-300 font-mono whitespace-pre-wrap leading-relaxed" data-testid="delivery-formula">
{`Fee Subtotal = Base Delivery Fee
             + (Number of Stops        × Per Stop Fee)
             + (Total Estimated Miles  × Per Mile Fee)
             + (Total Estimated Minutes × Per Mile Fee)   ← verbatim per spec §2
             + (Number of Stops        × Stop Wait Time Fee)

Tax          = Fee Subtotal × (Tax Rate / 100)   ← v2.7.63+E46 transparency
Total        = Fee Subtotal + Tax + Selected Driver Tip

If Total < Minimum Total Floor → Total = Minimum Total Floor

Driver take-home (on delivery close):
  driver_base    = (Fee Subtotal − Tax) × Driver Payout Ratio
  driver_tip     = Tip Amount   (drivers keep 100% of tips)
  driver_gross   = driver_base + driver_tip
  company_margin = Total − driver_gross − Tax

Admin override of Final Price mirrors into quote_total AND feeds the
End-of-Day deposit reconciliation. Full manual mode replaces the
Driver Payout Ratio calculation for that one delivery.`}
        </pre>
        <div className="mt-2 flex items-start gap-2 text-[11px] text-amber-300/90 bg-amber-500/5 border border-amber-400/30 rounded p-2">
          <ShieldAlert className="h-3.5 w-3.5 flex-shrink-0 mt-0.5"/>
          <div>
            <span className="font-bold">Note · verbatim §2:</span> the per-minute component multiplies by
            {" "}<code className="text-amber-200 font-bold">Per Mile Fee</code>, not Per Minute Fee. Preserving verbatim
            until the Operating Partner clarifies during the build. <code>Per Minute Fee</code> is stored on the
            config for future use — it does NOT currently participate in the calculation.
          </div>
        </div>
      </Card>

      {/* v2.7.57+E34 · Section 3 · Dispatch board (queue + driver-
          assignment dropdowns + per-order Document Center). Backed by
          `GET /api/deliveries/admin/list` with live refresh. */}
      <DeliveryDispatchBoard/>

      {/* v2.7.58+E37 · Section 5 · Payout ledger (auto-populated on
          driver mark-delivered) + FUTURE COURIER API INTEGRATION POINT
          placeholder config. */}
      <DeliveryPayoutLedger/>
      <CourierApiIntegrationCard/>
    </div>
  );
}
